import { useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconAIMagic } from '@components/icons/ai-magic';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

type AiBuilderReviewPromptProps = {
  // A run is in flight for this journey — from anywhere, including the panel's
  // own button — so no card should offer to start a second one.
  isRunning: boolean;
  onStart: () => void | Promise<unknown>;
  isLoading?: boolean;
};

export function AiBuilderReviewPrompt({
  isRunning,
  onStart,
  isLoading,
}: AiBuilderReviewPromptProps) {
  const { t } = useScopedTranslate(locale);
  // Acceptance belongs to this offer, not the journey — keyed off the live run, a
  // card reverts when the run ends and older cards claim credit for it.
  const [hasAccepted, setHasAccepted] = useState(false);
  const hasStarted = hasAccepted || isRunning;

  return (
    <ReviewCard>
      <Text
        font="regular14"
        color="textPrimary"
        text={hasStarted ? t('startedDescription') : t('description')}
      />
      <Footer>
        <Button
          variant="ai-gradient"
          iconLeft={<IconAIMagic color="aiGradientTeal100" />}
          text={hasAccepted ? t('started') : t('start')}
          onClick={async () => {
            setHasAccepted(true);
            try {
              await onStart();
            } catch {
              setHasAccepted(false);
            }
          }}
          isDisabled={hasStarted || isLoading}
        />
      </Footer>
    </ReviewCard>
  );
}

const ReviewCard = styled.div({
  width: '100%',
  border: `1px solid ${theme.color.border10}`,
  borderRadius: pxToRem(8),
  padding: pxToRem(16),
  background: theme.color.backgroundPrimary,
});

const Footer = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'flex-end',
  space: 2,
})({
  marginTop: pxToRem(12),
});

const locale = {
  en: {
    description:
      'I can review this journey for broken triggers, dead-end paths, unused fields and weak copy. It takes a minute or two.',
    startedDescription:
      "The review is running — findings will appear in the 'Review my journey' panel.",
    start: 'Review my journey',
    started: 'Review started',
  },
};
